import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Alert from '../../../../../../../../components/global/Alert';
import Button from '../../../../../../../../components/global/Button';
import Input from '../../../../../../../../components/global/Input';
import Spinner from '../../../../../../../../components/global/Spinner';
import getConfig from '../../../../../../../../util/config';
import sleep from '../../../../../../../../util/sleep';
import { getAsset } from '../../../../../../../../util/assets';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../../../constants/contributors';
import { BASENAME } from '../../../../../../../../constants/basenames';
import inIframe from '../../../../../../../../util/in-iframe';
import { ALL_PHOTOS_MODE } from '../../../../../../../../constants/album-folder';
import { getHierarchyItemJoinCode } from './helpers';
import ContextualString from '../../../../../../../../components/contextual/ContextualString';
import Badge from '../../../../../../../../components/global/Badge';

const propTypes = {
  hierarchyItem: PropTypes.shape({
    profileDisplayName: PropTypes.string,
    name: PropTypes.string.isRequired,
    album: PropTypes.shape({
      id: PropTypes.string.isRequired,
      join_code: PropTypes.string,
      has_password: PropTypes.bool.isRequired,
    }),
    album_folder: PropTypes.shape({
      id: PropTypes.string.isRequired,
      join_code: PropTypes.string,
      has_password: PropTypes.bool.isRequired,
    }),
  }),
  actionProps: PropTypes.shape({
    hierarchyItem: PropTypes.object,
    requireUserInteraction: PropTypes.bool,
  }),
  onRequestClose: PropTypes.func,
};

const ProOnboardingCTA = ({ hierarchyItem, actionProps, onRequestClose }) => {
  // By default, assume we want the user to actually interact with this CTA.
  // If a component specified otherwise, then we check for the appropiate conditions.
  // TODO: Refactor (DELETE) this component so that all this logic is not the job
  // of a modal.
  const { requireUserInteraction = true } = actionProps ?? {};
  const item = actionProps?.hierarchyItem ?? hierarchyItem;
  const entity = item?.album ?? item?.album_folder;
  const account = entity?.account;

  const { name: galleryName } = item;

  const defaultDisplayContent = (() => {
    if (entity?.has_password) {
      return true;
    }

    return !entity?.public_join_code_verification_enabled;
  })();

  const [loading, setLoading] = useState(false);
  const [displayContent, setDisplayContent] = useState(defaultDisplayContent);
  const [enteredJoinCode, setEnteredJoinCode] = useState('');
  const [error, setError] = useState('');
  const authenticated = useSelector(({ auth }) => auth.authenticated);

  const joinCode = getHierarchyItemJoinCode(item?.album_folder ?? item?.album);
  const isWebGallery =
    item?.album_folder?.is_web_gallery ?? item?.album?.is_web_gallery;

  const isPublicAllPhotosMode =
    item?.album_folder?.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC ||
    item?.album?.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC;

  // This condition has been established - if the user has entered a join code,
  // we allow them access to private galleries. Considering that the user would
  // have to first input a *valid* join code to see the content, this check
  // does fine for the use case.
  const isPictureDayAllPhotosPrivate =
    (!!enteredJoinCode || !!item?.join_code_access) &&
    (item?.album_folder?.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE ||
      item?.album?.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE);

  const isOnboardable =
    item?.album_folder?.joins_enabled || item?.album?.joins_enabled;

  const isUploadable =
    !!entity?.auto_contributor_settings?.upload_access &&
    entity?.auto_contributor_settings?.upload_access !==
      CONTRIBUTOR_UPLOAD_ACCESS.NONE;

  const isViewable =
    isWebGallery || isPictureDayAllPhotosPrivate || isPublicAllPhotosMode;

  const galleryId =
    item?.root_album_folder?.id ??
    item?.album?.root_album_folder?.id ??
    item?.id;

  // TODO: maybe just bubble these up and define in ProUserPage
  const startOnboarding = () => {
    window.open(
      `${getConfig().app.baseUrl}/discover/album-folder/${galleryId}`,
      '_blank',
    );

    onRequestClose?.();
  };

  const skipOnboarding = () => {
    if (isPublicAllPhotosMode) {
      window.open(
        `${getConfig().app.baseUrl}/consumer/matches/${galleryId}/all`,
        '_blank',
      );
    } else if (isPictureDayAllPhotosPrivate) {
      window.open(
        `${getConfig().app.baseUrl}/s/all-photos-access/${galleryId}`,
        '_blank',
      );
    } else {
      window.open(
        `${getConfig().app.baseUrl}${
          authenticated
            ? `/app/${BASENAME.SHUTTERBUG}/hierarchy-items/${galleryId}`
            : `/galleries/view/folder/${galleryId}`
        }`,
        '_blank',
      );
    }

    onRequestClose?.();
  };

  const uploadPhotos = () => {
    window.open(
      `${getConfig().app.baseUrl}/contributors/u/${galleryId}`,
      '_blank',
    );

    onRequestClose?.();
  };

  useEffect(() => {
    if (!entity?.id) {
      return;
    }
    // Will move to WG automatically assuming no interaction is required.
    if (
      defaultDisplayContent &&
      !isOnboardable &&
      !isUploadable &&
      !requireUserInteraction
    ) {
      skipOnboarding();
      // Will move to Onboarding automatically assuming no interaction is required.
    } else if (
      defaultDisplayContent &&
      !isViewable &&
      !isUploadable &&
      !requireUserInteraction
    ) {
      startOnboarding();
      // Will move to Uploading automatically assuming no interaction is required.
    } else if (
      defaultDisplayContent &&
      !isViewable &&
      !isOnboardable &&
      !requireUserInteraction
    ) {
      uploadPhotos();
    }
  }, [defaultDisplayContent, entity?.id]);

  const handleJoinCodeSubmit = async () => {
    setError('');
    // Wait a moment (instant UIs are too fast for good UX).
    setLoading(true);
    await sleep(350);

    // Validate entered code, before swapping to left/right door content.
    if (
      enteredJoinCode.trim().toLowerCase() === joinCode.trim().toLowerCase()
    ) {
      if (!isWebGallery && isOnboardable) {
        startOnboarding();
        return;
      }

      if (isWebGallery && !isOnboardable) {
        skipOnboarding();
      }

      setDisplayContent(true);
    } else {
      setError(`Sorry, that's not the right Join Code.`);
    }
    setLoading(false);
  };

  const profileLinkProps = inIframe()
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  if (!item || !entity) {
    return null;
  }

  return (
    <div className="pro-onboarding">
      <p className="gallery-welcome">Welcome To</p>
      <h3 className="gallery-title">{galleryName}</h3>
      {account?.username && (
        <p className="text-xs d-flex align-items-center">
          by{' '}
          <Button
            btnStyle="link"
            to={`/@${account.username}`}
            className="ml-1"
            {...profileLinkProps}
          >
            @{account.username}
          </Button>{' '}
        </p>
      )}

      <hr />

      {!displayContent && (
        <Fragment>
          <form
            onSubmit={event => {
              event.preventDefault();
              handleJoinCodeSubmit();
            }}
          >
            <h5 className="mt-3">Enter the gallery Join Code</h5>
            <Input
              id="join-code-input"
              type="text"
              value={enteredJoinCode}
              onChange={event => setEnteredJoinCode(event.target.value)}
              className="mb-2"
            />

            {error && (
              <Alert className="mt-2 mb-0" type="danger">
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              className="mt-2"
              disabled={!enteredJoinCode || loading}
            >
              {loading ? <Spinner light size="xs" /> : 'Join'}
            </Button>

            {loading}
          </form>
        </Fragment>
      )}
      {displayContent && (
        <div className="pro-onboarding-actions">
          {isOnboardable && (
            <div className="pro-onboarding-action">
              <Badge badgeStyle="yellow" badgeSize="xs" className="text-bold">
                POPULAR!
              </Badge>
              <img src={getAsset('/waldo-way.png')} alt="Find my photos" />
              <div className="pro-onboarding-action-content">
                <p className="text-extra-bold">
                  Gallery viewing
                  <br />
                  (with auto-tagging)
                </p>
                <p className="text-xs text-light">
                  <ContextualString
                    id="waldoFindPhotosYouAreInTitle"
                    type={entity.album_type}
                    subtype={entity.album_subtype}
                    defaultMessage="Enjoy the gallery but let Waldo do the \n hunting and pecking! All your \n photos sent via text alert when found."
                  />
                </p>
                <Button btnBlock onClick={startOnboarding}>
                  Activate auto-tagging
                </Button>
              </div>
            </div>
          )}
          {isViewable && (
            <div className="pro-onboarding-action">
              <img src={getAsset('/waldo-view-photos.png')} alt="View Photos" />
              <div className="pro-onboarding-action-content">
                <p className="text-extra-bold">
                  Gallery viewing
                  <br />
                  (manual search)
                </p>
                <p className="text-xs text-light">
                  View all the photos and search for yours.
                </p>
                <Button btnBlock onClick={skipOnboarding}>
                  Search gallery
                </Button>
              </div>
            </div>
          )}
          {isUploadable && (
            <div className="pro-onboarding-action">
              <img
                src={getAsset('/upload-photos-illustration.svg')}
                alt="Upload Photos"
              />
              <div className="pro-onboarding-action-content">
                <p className="text-extra-bold">
                  Snapped photos? <br /> Add yours here!
                </p>
                <p className="text-xs text-light">
                  Upload photos of the event and share them with everyone!
                </p>
                <Button btnBlock onClick={uploadPhotos}>
                  Upload now
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ProOnboardingCTA.propTypes = propTypes;

export default ProOnboardingCTA;
